import { useState } from "react";

type Primitive = string | number | boolean | null | undefined | symbol | bigint;

export function useSelection<T extends Primitive>(
  initialSelection: T[],
  areItemsEqual?: never // No findFunction allowed for primitives
): [T[], (item: T) => void, () => void];

export function useSelection<T>(
  initialSelection: T[],
  areItemsEqual: (item1: T, item2: T) => boolean // Required for objects
): [T[], (item: T) => void, () => void];

export function useSelection<T>(
  initialSelection: T[],
  areItemsEqual?: (item1: T, item2: T) => boolean
) {
  const [selection, setSelection] = useState(initialSelection);

  const toggleSelection = (item: T) => {
    let newSelection: T[];

    if (areItemsEqual) {
      // Objects: use custom equality function
      const isSelected = selection.some((selectedItem) => areItemsEqual(selectedItem, item));

      if (isSelected) {
        newSelection = selection.filter((selectedItem) => !areItemsEqual(selectedItem, item));
      } else {
        newSelection = [...selection, item];
      }
    } else {
      // Primitives: use built-in includes
      const isSelected = selection.includes(item);

      if (isSelected) {
        newSelection = selection.filter((selectedItem) => selectedItem !== item);
      } else {
        newSelection = [...selection, item];
      }
    }

    setSelection(newSelection);
  };

  const emptySelection = () => setSelection([]);

  return [selection, toggleSelection, emptySelection];
}
